import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Flex, Text } from "styles/utility-components";
import { validate as isValidUUID } from "uuid";

import { BuyerSelect } from "components/form_components/BuyerSelect";
import Button from "components/ui/button/Button";
import Input from "components/ui/input/Input";
import TextAreaInput from "components/ui/input/TextAreaInput";
import Modal from "components/ui/modal/Modal";
import { toast } from "components/ui/toast/Toast";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { StotlesCalloutBlockProps } from "../custom_blocks/stotlesCalloutBlock";

type AddStotlesCalloutModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: StotlesCalloutBlockProps) => void;
};

type AddStotlesCalloutFormValues = StotlesCalloutBlockProps & {
  selectedBuyerGuid?: string;
};

interface BuyerSelectOption {
  label?: string;
  value?: string | number;
}

const defaultFormValues: AddStotlesCalloutFormValues = {
  selectedBuyerGuid: undefined,
  orgName: "",
  userCompanyName: "",
  body: "",
  orgGuid: "",
  orgType: "buyer",
};

export default function AddStotlesCalloutModal({
  isOpen,
  onClose,
  onSubmit,
}: AddStotlesCalloutModalProps) {
  const currentUserOrgName = useAssertCurrentUser().company.name;

  const {
    control,
    setValue,
    reset,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm<AddStotlesCalloutFormValues>({
    defaultValues: defaultFormValues,
    mode: "onChange",
  });

  useEffect(() => {
    if (isOpen) {
      reset({
        ...defaultFormValues,
        userCompanyName: currentUserOrgName,
      });
    }
  }, [isOpen, reset, currentUserOrgName]);

  const handleClose = () => {
    reset(defaultFormValues);
    onClose();
  };

  const submitForm = handleSubmit((values) => {
    const trimmedObject = {
      orgGuid: values.orgGuid,
      orgName: values.orgName.trim(),
      orgType: values.orgType,
      userCompanyName: values.userCompanyName.trim(),
      body: values.body.trim(),
    };
    if (Object.values(trimmedObject).some((value) => value === "")) {
      toast.error("Fields need to be filled with non-empty values.");
      return;
    }

    onSubmit(trimmedObject);
    handleClose();
  });

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
    >
      <Flex flexDirection="column" gap={16} style={{ width: 480, padding: 16 }}>
        <Text h3>Add Stotles callout</Text>
        <BuyerSelect
          name="selectedBuyerGuid"
          label="Buyer"
          control={control}
          placeholder="Search buyers"
          selectAll={false}
          onChange={(values, option) => {
            const selectedBuyerGuid = values?.toString() ?? "";
            const selectedOption = Array.isArray(option) ? option[0] : option;
            const selectedBuyerName =
              (selectedOption as BuyerSelectOption | undefined)?.label?.toString() ?? "";

            // Having both orgGuid & selectedBuyerGuid is technically redundant but doing
            // since this value can't be an empty string and it simplifies this internal facing form
            if (selectedBuyerName !== "") {
              setValue("orgName", selectedBuyerName);
              setValue("orgGuid", selectedBuyerGuid, { shouldValidate: true });
            } else if (selectedBuyerGuid === "") {
              setValue("orgName", "");
              setValue("orgGuid", "");
            }
          }}
        />
        <Input
          name="orgName"
          control={control}
          aria-label="Buyer name"
          placeholder="Buyer name"
          rules={{ required: "Organisation name is required" }}
        />
        <Input
          name="userCompanyName"
          control={control}
          aria-label="User company name"
          placeholder="User company name"
          rules={{ required: "User company name is required" }}
        />
        <TextAreaInput
          name="body"
          control={control}
          aria-label="Body"
          placeholder="Body"
          minRows={4}
          rules={{ required: "Body is required" }}
        />
        <Input
          name="orgGuid"
          control={control}
          aria-label="Organisation GUID"
          placeholder="Organisation GUID"
          rules={{
            required: "Organisation GUID is required",
            validate: (value) =>
              isValidUUID(value ?? "") || "Organisation GUID must be a valid UUID",
          }}
        />
        <Flex justifyContent="flex-end" gap={8}>
          <Button buttonType="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            buttonType="primary"
            onClick={submitForm}
            loading={isSubmitting}
            disabled={!isValid}
          >
            Insert callout
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
}
